import React, { useEffect, useRef, useState } from 'react';

import '../accountSetup/scss/_accountSetup.scss';

import { useSelector } from 'react-redux';
import store, { brokerActions } from '@apply/adminStore';
import { ConfirmV2 } from '@apply/ui';
import { Panel, PanelBorderRadius, PanelBorderStyle, PanelTheme } from '@apply/components';
import { ValidationGroup } from 'devextreme-react';
import { Header, MenuItemOption } from '../../common_v2/components/Header';
import { Footer } from '../../common_v2/components/Footer';
import { useLocation } from 'react-router-dom';

import { useBrokerDetailsStore } from '../state/brokerDetailsStore';
import { AdditionalInformationPanel } from './components/AdditionalInformationPanel/AdditionalInformationPanel';
import { ContactAddressSection } from '../components/ContactAddressSection/ContactAddressSection';
import { HomeAddressSection } from '../components/HomeAddressSection/HomeAddressSection';
import { DateOfBirthField } from '../components/DateOfBirthField';

import { useBrokerAccountSetupInit } from './hooks/useBrokerAccountSetupInit';
import { useBrokerAccountSetupValidation } from './hooks/useBrokerAccountSetupValidation';
import { useBrokerAccountSetupSubmit } from './hooks/useBrokerAccountSetupSubmit';

import { BrokerInfoPanel } from './components/BrokerInfoPanel/BrokerInfoPanel';
import { BankWirePanel } from '../components/BankWirePanel/BankWirePanel';
import { W9Panel } from './components/W9Panel/W9Panel';
import { ESignPanel } from './components/ESignPanel/ESignPanel';
import { ActionButtonsSection } from './components/ActionButtonsSection';
import { isMobileScreen } from '@apply/ui';
import { useNavigate } from 'react-router-dom';

export const AccountSetup: React.FC = () => {
    const navigate = useNavigate();
    const mobileScreen = isMobileScreen();
    /* eslint-disable @typescript-eslint/no-explicit-any */
    // Redux state is typed as any
    const bfiUser = useSelector((state: any) => state.bfiUser);

    if (!bfiUser) {
        navigate('/login');
    }
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const storeId = useBrokerDetailsStore((s) => s.id);
    const location = useLocation();

    const vgRef = useRef<ValidationGroup>(null);
    const [showCompanyPanel, setShowCompanyPanel] = useState(false);
    const [showBackgroundCheckDate, setShowBackgroundCheckDate] = useState(false);
    const [isConfirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
    const [isRejectConfirmOpen, setRejectConfirmOpen] = useState(false);
    const [rejectComment, setRejectComment] = useState('');

    useBrokerAccountSetupInit();
    const { validate } = useBrokerAccountSetupValidation();
    const submit = useBrokerAccountSetupSubmit(() => validate({ showCompanyPanel, showBackgroundCheckDate, vgRef }), {
        setConfirmSubmitOpen,
        setRejectConfirmOpen,
    });

    useEffect(() => {
        const s = useBrokerDetailsStore.getState();
        setShowCompanyPanel(!!s.businessName);
        setShowBackgroundCheckDate(!!s.backgroundCheckDate);
    }, [storeId]);

    const headerText = bfiUser.isBroker
        ? mobileScreen
            ? 'Review Account'
            : 'Review Account Information'
        : storeId
          ? 'Edit Broker'
          : 'Add Broker';
    const isCenteredLayout = location.pathname === '/account';

    const handleClose = () => {
        store.dispatch(brokerActions.setBrokerModalOpen({ openBrokerModal: false }));
    };

    const handleOpenRejectConfirm = () => {
        setRejectComment('');
        setRejectConfirmOpen(true);
    };

    const handleCloseRejectConfirm = () => {
        setRejectConfirmOpen(false);
        setRejectComment('');
    };

    const disclaimerText: string = bfiUser.isAdmin
        ? 'Brokers are not able to change all fields. They may contact BFI to request changes to those fields.'
        : 'Brokers are not able to change all fields. Please contact BFI to request changes to those fields if needed.';

    return (
        <>
            <div className='app-container'>
                <Header activeMenuItem={MenuItemOption.ReviewAccountInfo} />
                <main>
                    <div
                        role='banner'
                        className='broker-details-banner'
                    >
                        <br />
                        <h1
                            className={bfiUser.admin ? 'page-header-single-h1' : ''}
                            style={mobileScreen ? { paddingLeft: 16 } : undefined}
                        >
                            {headerText}
                        </h1>
                        <br />
                        {bfiUser.admin && (
                            <button
                                type='button'
                                className='close'
                                data-dismiss='modal'
                                aria-label='Close'
                                onClick={handleClose}
                            >
                                <span aria-hidden='true'>&times;</span>
                            </button>
                        )}
                    </div>
                    <Panel
                        theme={PanelTheme.White}
                        borderRadius={PanelBorderRadius.Medium}
                        borderStyle={PanelBorderStyle.Solid}
                    >
                        <ValidationGroup
                            id='vgBrokerAccountSetup'
                            ref={vgRef}
                        >
                            <div className='broker-details-content'>
                                <div className={isCenteredLayout ? 'broker-details-centered' : undefined}>
                                    <p className='top-disclaimer'>{disclaimerText}</p>
                                    <br />
                                    <BrokerInfoPanel
                                        showCompanyPanel={showCompanyPanel}
                                        onShowCompanyPanelChange={setShowCompanyPanel}
                                        showBackgroundCheckDate={showBackgroundCheckDate}
                                        onShowBackgroundCheckDateChange={setShowBackgroundCheckDate}
                                    />
                                    <br />
                                    <BankWirePanel />
                                    {bfiUser.isBroker && (
                                        <>
                                            <ContactAddressSection />
                                            <br />
                                            <HomeAddressSection />
                                        </>
                                    )}
                                    {!bfiUser.isBroker && <AdditionalInformationPanel />}
                                    <br />
                                    <W9Panel />
                                    <br />
                                    <ESignPanel />
                                </div>
                                <div className={`button-footer ${isCenteredLayout ? 'broker-details-centered' : ''}`}>
                                    <ActionButtonsSection
                                        onSave={submit.handleSave}
                                        onActivate={submit.handleActivate}
                                        onReject={handleOpenRejectConfirm}
                                        onCancel={handleClose}
                                    />
                                </div>
                            </div>
                        </ValidationGroup>
                    </Panel>

                    {isConfirmSubmitOpen && (
                        <ConfirmV2
                            id='confirmSubmitModal'
                            open={true}
                            title='Confirm Submit'
                            onConfirm={submit.confirmSubmit}
                            onCancel={() => setConfirmSubmitOpen(false)}
                            okLabel='Submit'
                            width='450px'
                        >
                            <p>
                                If all information supplied is accurate, click submit to finish and submit the file to BFI for
                                review & approval.
                            </p>
                        </ConfirmV2>
                    )}

                    {isRejectConfirmOpen && (
                        <ConfirmV2
                            id='rejectConfirmModal'
                            open={true}
                            title='Reject Broker'
                            onConfirm={() => submit.handleReject(rejectComment)}
                            onCancel={handleCloseRejectConfirm}
                            okLabel='Reject'
                            width='450px'
                        >
                            <div>
                                <label htmlFor='rejectedComment'>Reason for rejection:</label>
                                <textarea
                                    id='rejectedComment'
                                    cols={40}
                                    rows={5}
                                    value={rejectComment}
                                    onChange={(e) => setRejectComment(e.target.value)}
                                    className='reject-comment-textarea'
                                />
                            </div>
                        </ConfirmV2>
                    )}
                </main>
            </div>
            <Footer showAuthenticatedFooter={true} />
        </>
    );
};

export default AccountSetup;
